import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../Supabase/SupabaseClient'; // Ajusta la ruta según tu estructura
import './auth.css'; // Asegúrate de importar los estilos
import Footer from "../../Components/Footer/Footer";

function Registro() {
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        telefono: '',
        direccion: '',
        dni: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const { nombre, email, telefono, direccion, dni, password, confirmPassword } = formData;

        // Validar campos requeridos
        if (!nombre.trim() || !email.trim() || !telefono.trim() || !direccion.trim() || !dni.trim() || !password || !confirmPassword) {
            setError('Todos los campos son obligatorios');
            return false;
        }

        // Validar contraseñas
        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return false;
        }

        if (password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return false;
        }

        // Validar DNI (formato básico - ajusta según tu país)
        if (dni.length < 7 || dni.length > 8 || !/^\d+$/.test(dni)) {
            setError('El DNI debe tener entre 7 y 8 dígitos');
            return false;
        }

        // Validar teléfono (formato básico)
        if (telefono.length < 10 || !/^\d+$/.test(telefono.replace(/[\s\-()]/g, ''))) {
            setError('El teléfono debe tener al menos 10 dígitos');
            return false;
        }

        return true;
    };

    const handleRegistro = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        try {
            const { nombre, email, telefono, direccion, dni, password } = formData;

            // 1. Crear usuario en Supabase Auth
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email,
                password
            });

            if (authError) throw authError;

            // 2. Insertar datos adicionales en la tabla usuarios
            if (authData.user) {
                const { error: insertError } = await supabase
                    .from('usuarios')
                    .insert([
                        {
                            id: authData.user.id,
                            nombre: nombre.trim(),
                            email: email.trim(),
                            telefono: telefono.trim(),
                            direccion: direccion.trim(),
                            dni: dni.trim()
                        }
                    ]);

                if (insertError) {
                    // Si falla la inserción, eliminar el usuario de auth
                    await supabase.auth.admin.deleteUser(authData.user.id);
                    throw insertError;
                }
            }

            // Mostrar mensaje de verificación de correo
            alert('¡Registro exitoso! Por favor, verifica tu correo electrónico para completar el registro.');
            
            // Redirigir al login
            navigate('/login');
        } catch (error) {
            console.error('Error en registro:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <div className="auth-container">
            <div className="auth-form-wrapper">
                <h2 className="auth-form-title">Crear Cuenta</h2>
                
                {error && <div className="auth-error-message">{error}</div>}
                
                <form onSubmit={handleRegistro} className="auth-form">
                    <div className="form-group">
                        <label htmlFor="nombre">Nombre Completo</label>
                        <input
                            type="text"
                            id="nombre"
                            name="nombre"
                            className="form-input"
                            value={formData.nombre}
                            onChange={handleInputChange}
                            required
                            placeholder="Ingresa tu nombre completo"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="email">Correo Electrónico</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-input"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="tu@email.com"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="telefono">Teléfono</label>
                        <input
                            type="tel"
                            id="telefono"
                            name="telefono"
                            className="form-input"
                            value={formData.telefono}
                            onChange={handleInputChange}
                            required
                            placeholder="Ej: 1234567890"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="direccion">Dirección</label>
                        <input
                            type="text"
                            id="direccion"
                            name="direccion"
                            className="form-input"
                            value={formData.direccion}
                            onChange={handleInputChange}
                            required
                            placeholder="Calle, número, ciudad"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="dni">DNI</label>
                        <input
                            type="text"
                            id="dni"
                            name="dni"
                            className="form-input"
                            value={formData.dni}
                            onChange={handleInputChange}
                            required
                            placeholder="12345678"
                            maxLength="8"
                        />
                        <small className="field-note">
                            * El DNI será utilizado para generar contratos al contratar servicios
                        </small>
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="password">Contraseña</label>
                        <div className="password-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                className="form-input"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                minLength={6}
                                placeholder="Mínimo 6 caracteres"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(prev => !prev)}
                                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                aria-pressed={showPassword}
                            >
                                {showPassword ? <EyeOff className="password-toggle-icon" /> : <Eye className="password-toggle-icon" />}
                            </button>
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmar Contraseña</label>
                        <div className="password-wrapper">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                name="confirmPassword"
                                className="form-input"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                required
                                minLength={6}
                                placeholder="Repite tu contraseña"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(prev => !prev)}
                                aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                aria-pressed={showConfirmPassword}
                            >
                                {showConfirmPassword ? <EyeOff className="password-toggle-icon" /> : <Eye className="password-toggle-icon" />}
                            </button>
                        </div>
                    </div>
                    
                    <button 
                        type="submit" 
                        className="auth-submit-btn"
                        disabled={loading}
                    >
                        {loading ? 'Registrando...' : 'Crear Cuenta'}
                    </button>
                </form>
                
                <div className="auth-switch-text">
                    ¿Ya tienes cuenta?
                    <Link to="/login" className="auth-switch-link">
                        Inicia Sesión
                    </Link>
                </div>
            </div>
        </div>
        <Footer />
        </>
    );
}

export default Registro;